import React, { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import Header from "@/components/Header";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Manifesto from "@/components/Manifesto";
import HeroDiscount from "@/components/HeroDiscount";
import OurWork from "@/components/OurWork";
import Estimate from "@/components/Estimate";
import Services from "@/components/Services";
import QuoteDialog from "@/components/QuoteDialog";
import Footer from "@/components/Footer";
import AdminPage from "@/components/AdminPage";
import { DEFAULT_SETTINGS } from "@/data/siteContent";
import { api } from "@/lib/api";
import useSmoothScroll from "@/hooks/useSmoothScroll";

function PublicPage({ settings, language, setLanguage }) {
  const [mode, setMode] = useState("heat");
  const [quoteOpen, setQuoteOpen] = useState(false);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    const nodes = document.querySelectorAll(".reveal");
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => entry.isIntersecting && entry.target.classList.add("is-visible")), { threshold: 0.12 });
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [settings, language]);

  return (
    <div className={`site-shell ${mode === "cool" ? "cool-mode" : ""}`} data-testid="public-site">
      <Header settings={settings} language={language} setLanguage={setLanguage} mode={mode} setMode={setMode} />
      <main>
        <Hero settings={settings} language={language} mode={mode} onQuote={() => setQuoteOpen(true)} />
        <Marquee language={language} />
        <Manifesto settings={settings} language={language} />
        <HeroDiscount settings={settings} language={language} onQuote={() => setQuoteOpen(true)} />
        <OurWork settings={settings} language={language} onQuote={() => setQuoteOpen(true)} />
        <Estimate language={language} mode={mode} onQuote={() => setQuoteOpen(true)} />
        <Services language={language} />
        <section className="contact-section" id="contact" data-testid="contact-section">
          <div className="section-shell contact-layout reveal">
            <div><span className="section-label">{language === "en" ? "05 // Start a conversation" : "05 // Inicia la conversación"}</span><h2 className="contact-title">{language === "en" ? "Good climate changes everything." : "Un buen clima lo cambia todo."}</h2><p className="contact-copy">{language === "en" ? "A clear next step is a call or a message. Tell us what your space needs, and we will take it from there." : "El siguiente paso es simple: una llamada o un mensaje. Cuéntanos qué necesita tu espacio y partimos desde ahí."}</p></div>
            <div className="contact-actions"><a className="contact-card" href={`tel:${settings.phone.replace(/[^0-9+]/g, "")}`} data-testid="contact-call-card"><span className="contact-card-label">{language === "en" ? "Talk now" : "Habla ahora"}</span><PhoneGlyph /><span className="contact-card-value">{settings.phone}</span></a><button className="contact-card" type="button" onClick={() => setQuoteOpen(true)} data-testid="contact-request-card"><span className="contact-card-label">{language === "en" ? "Send a request" : "Enviar solicitud"}</span><MailGlyph /><span className="contact-card-value">{language === "en" ? "Request estimate" : "Solicitar cotización"}</span></button></div>
          </div>
        </section>
      </main>
      <Footer settings={settings} language={language} />
      {quoteOpen && <QuoteDialog settings={settings} language={language} onClose={() => setQuoteOpen(false)} />}
    </div>
  );
}

function PhoneGlyph() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 3.08 5.18 2 2 0 0 1 5.06 3h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.62 2.63a2 2 0 0 1-.45 2.11L9 10.73a16 16 0 0 0 4.27 4.27l1.27-1.23a2 2 0 0 1 2.11-.45c.85.29 1.73.5 2.63.62A2 2 0 0 1 22 16.92Z" /></svg>; }
function MailGlyph() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="1" /><path d="m3 7 9 6 9-6" /></svg>; }

export default function App() {
  useSmoothScroll();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [language, setLanguage] = useState(() => window.localStorage.getItem("protech_lang") || "en");

  useEffect(() => {
    window.localStorage.setItem("protech_lang", language);
  }, [language]);

  useEffect(() => {
    api.get("/public/settings").then(({ data }) => setSettings(data)).catch(() => undefined);
  }, []);

  return (
    <BrowserRouter>
      <Toaster theme="dark" position="bottom-right" />
      <Routes>
        <Route path="/" element={<PublicPage settings={settings} language={language} setLanguage={setLanguage} />} />
        <Route path="/admin" element={<AdminPage onSettingsSaved={setSettings} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
